export const INPUT_FIELDS = [
  { key: 'brand', label: 'Brand', type: 'text', required: true },
  { key: 'packaging_weight', label: 'Packaging weight (g)', min: 0, required: true },
  { key: 'moq_packs', label: 'MOQ (No. of packs)', min: 1, step: 1, required: true },
  { key: 'packaging_cost', label: 'Packaging cost/pack (₹)', min: 0, required: true },
  { key: 'label_cost', label: 'Label cost/pack (₹)', min: 0, required: true },
  { key: 'required_margin_pct', label: 'Required gross margin (%)', min: 0, max: 99.99, required: true },
  { key: 'approved_price_pack', label: 'Approved price/pack excl. GST (₹)', min: 0, required: true },
  { key: 'portion_qty', label: 'Assumed quantity/portion (g)', min: 0, required: false },
];

export const COLUMNS = [
  { key: 'sku_id', label: 'SKU ID', type: 'text', pinned: true },
  { key: 'sku_name', label: 'SKU Name', type: 'text', pinned: true },
  { key: 'brand', label: 'Brand', type: 'text', pinned: true },
  { key: 'packaging_weight', label: 'Packaging weight (g)' },
  { key: 'moq_packs', label: 'MOQ (No. of packs)', digits: 0 },
  { key: 'moq_total_gms', label: 'MOQ (Total gms)', formula: 'Weight per pack × MOQ packs' },
  { key: 'ep_rate_gm', label: 'EP rate/gm', currency: true, digits: 6, formula: 'Current L2 EP cost per gram' },
  { key: 'cogs_pack', label: 'COGS/pack', currency: true, formula: 'Weight per pack × EP rate/gm' },
  { key: 'packaging_cost', label: 'Packaging cost/pack', currency: true },
  { key: 'label_cost', label: 'Label cost/pack', currency: true },
  { key: 'total_cost_pack', label: 'Total cost/pack', currency: true, formula: 'COGS + packaging + label cost per pack' },
  { key: 'required_margin_pct', label: 'Required margin %', percent: true },
  { key: 'customer_price_pack', label: 'Customer price/pack (excl. GST)', currency: true, formula: 'Total cost/pack ÷ (1 − required margin / 100)' },
  { key: 'projected_moq_margin', label: 'Projected MOQ margin amount', currency: true, formula: '(Customer price/pack − total cost/pack) × MOQ packs' },
  { key: 'approved_price_pack', label: 'Approved price/pack (excl. GST)', currency: true },
  { key: 'approved_price_moq', label: 'Approved price/MOQ', currency: true, formula: 'Approved price/pack × MOQ packs' },
  { key: 'actual_margin_pct', label: 'Actual margin %', percent: true, formula: '(Approved revenue − total MOQ cost) ÷ approved revenue × 100' },
  { key: 'actual_margin_amount', label: 'Actual margin amount', currency: true, formula: 'Approved revenue − total cost/pack × MOQ packs' },
  { key: 'portion_qty', label: 'Assumed quantity/portion (g)' },
  { key: 'cost_per_portion', label: 'Cost per portion', currency: true, formula: 'Approved price/MOQ ÷ MOQ total grams × portion grams' },
];

export const blankPricing = () => Object.fromEntries(['product_id', ...INPUT_FIELDS.map(f => f.key)].map(key => [key, '']));
const number = value => value === '' || value == null || !Number.isFinite(Number(value)) ? null : Number(value);
const finite = value => Number.isFinite(value) ? value : null;

export function calculatePricing(input, epRate) {
  const w = number(input.packaging_weight), n = number(input.moq_packs), ep = number(epRate);
  const packaging = number(input.packaging_cost), label = number(input.label_cost);
  const margin = number(input.required_margin_pct), price = number(input.approved_price_pack), portion = number(input.portion_qty);
  const weight = w > 0 && n > 0 ? finite(w * n) : null;
  const cogs = w > 0 && ep !== null ? finite(w * ep) : null;
  const cost = cogs !== null && packaging !== null && label !== null ? finite(cogs + packaging + label) : null;
  const suggested = cost !== null && margin !== null && margin >= 0 && margin < 100 ? finite(cost / (1 - margin / 100)) : null;
  const revenue = price > 0 && n > 0 ? finite(price * n) : null;
  const profit = revenue !== null && cost !== null ? finite(revenue - cost * n) : null;
  return {
    moq_total_gms: weight, ep_rate_gm: ep, cogs_pack: cogs, total_cost_pack: cost,
    customer_price_pack: suggested, projected_moq_margin: suggested !== null && n > 0 ? finite((suggested - cost) * n) : null,
    approved_price_moq: revenue, actual_margin_pct: profit !== null && revenue > 0 ? finite(profit / revenue * 100) : null,
    actual_margin_amount: profit, cost_per_portion: revenue !== null && weight > 0 && portion > 0 ? finite(revenue / weight * portion) : null,
  };
}

export function validatePricing(input, product) {
  const errors = {};
  if (!product) errors.product_id = 'Select an existing L2 product.';
  else if (product.source_error) errors.product_id = product.source_error;
  for (const f of INPUT_FIELDS) {
    const value = input[f.key];
    if (value === '' || value == null) {
      if (f.required) errors[f.key] = 'Required.';
      continue;
    }
    if (f.type === 'text') {
      if (!/^[\p{L}\p{N} ]+$/u.test(value.trim()) || !value.trim() || value.trim().length > 80) errors[f.key] = 'Use letters, numbers and spaces (max. 80 characters).';
      continue;
    }
    const v = Number(value);
    if (!Number.isFinite(v) || v < 0 || v > 1e9) errors[f.key] = 'Enter a valid non-negative number up to 1 billion.';
    if (['packaging_weight', 'approved_price_pack', 'portion_qty'].includes(f.key) && v <= 0) errors[f.key] = 'Must be greater than zero.';
    if (f.key === 'moq_packs' && (!Number.isInteger(v) || v < 1)) errors[f.key] = 'Enter a positive whole number of packs.';
    if (f.key === 'required_margin_pct' && (v < 0 || v >= 100)) errors[f.key] = 'Margin must be at least 0 and below 100%.';
  }
  return errors;
}

export const pricingPayload = input => Object.fromEntries(['product_id', ...INPUT_FIELDS.map(f => f.key)].map(key => [key,
  ['product_id', 'brand'].includes(key) ? input[key].trim().replace(/\s+/g, ' ') : input[key] === '' || input[key] == null ? null : Number(input[key]),
]));

export function formatPricing(value, column, plain = false) {
  if (value == null || value === '' || (column.type !== 'text' && !Number.isFinite(Number(value)))) return plain ? '' : '—';
  if (column.type === 'text') return String(value);
  const formatted = plain ? Number(value).toFixed(column.digits ?? 2) : Number(value).toLocaleString('en-IN', { minimumFractionDigits: column.digits ?? 2, maximumFractionDigits: column.digits ?? 2 });
  return `${!plain && column.currency ? '₹' : ''}${formatted}${!plain && column.percent ? '%' : ''}`;
}